import { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Loader2,
  Save,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Pencil,
  ExternalLink,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const SECTION_TYPES = [
  { type: "hero", label: "Hero" },
  { type: "video", label: "YouTube Video" },
  { type: "features", label: "Features (3 points)" },
  { type: "pricing", label: "Pricing" },
  { type: "footer", label: "Footer" },
  { type: "text", label: "Custom Text" },
];

const FIELD_SPECS = {
  hero: [
    { k: "eyebrow", label: "Eyebrow label (small text above heading)", type: "input" },
    { k: "headline", label: "Headline", type: "textarea" },
    { k: "subheadline", label: "Sub-headline", type: "textarea" },
    { k: "primary_cta_text", label: "Primary button text", type: "input" },
    { k: "primary_cta_href", label: "Primary button link", type: "input", placeholder: "/signup" },
    { k: "secondary_cta_text", label: "Secondary button text", type: "input" },
    { k: "secondary_cta_href", label: "Secondary button link", type: "input", placeholder: "/login" },
  ],
  video: [
    { k: "eyebrow", label: "Eyebrow label", type: "input" },
    { k: "title", label: "Title", type: "input" },
    { k: "subtitle", label: "Subtitle", type: "input" },
    { k: "youtube_url", label: "YouTube URL", type: "input", placeholder: "https://www.youtube.com/watch?v=..." },
  ],
  features: [
    { k: "eyebrow", label: "Eyebrow label", type: "input" },
    { k: "title", label: "Title", type: "input" },
    { k: "subtitle", label: "Subtitle", type: "input" },
    { k: "features", label: "Feature points", type: "features" },
  ],
  pricing: [
    { k: "eyebrow", label: "Eyebrow label", type: "input" },
    { k: "title", label: "Title", type: "input" },
    { k: "subtitle", label: "Subtitle", type: "input" },
    { k: "plan_name", label: "Plan name", type: "input" },
    { k: "price_text", label: "Price (e.g. ₹999)", type: "input" },
    { k: "price_period", label: "Price period (e.g. / month)", type: "input" },
    { k: "included", label: "Line 1 — included", type: "input" },
    { k: "overage", label: "Line 2 — overage", type: "input" },
    { k: "cta_text", label: "Button text", type: "input" },
    { k: "cta_href", label: "Button link", type: "input" },
  ],
  footer: [
    { k: "tagline", label: "Tagline", type: "input" },
    { k: "support_email", label: "Support email", type: "input", placeholder: "support@bulkinvoice.in" },
    { k: "copyright", label: "Copyright line", type: "input" },
  ],
  text: [
    { k: "eyebrow", label: "Eyebrow label", type: "input" },
    { k: "title", label: "Title", type: "input" },
    { k: "body", label: "Body", type: "textarea" },
  ],
};

const EMPTY_DATA = {
  hero: {
    eyebrow: "",
    headline: "",
    subheadline: "",
    primary_cta_text: "Start Free Trial",
    primary_cta_href: "/signup",
    secondary_cta_text: "",
    secondary_cta_href: "/login",
  },
  video: { eyebrow: "", title: "", subtitle: "", youtube_url: "" },
  features: {
    eyebrow: "",
    title: "",
    subtitle: "",
    features: [
      { title: "", body: "" },
      { title: "", body: "" },
      { title: "", body: "" },
    ],
  },
  pricing: {
    eyebrow: "",
    title: "",
    subtitle: "",
    plan_name: "Monthly",
    price_text: "₹999",
    price_period: "/ month",
    included: "",
    overage: "",
    cta_text: "Start Free Trial",
    cta_href: "/signup",
  },
  footer: { tagline: "", support_email: "", copyright: "" },
  text: { eyebrow: "", title: "", body: "" },
};

function newSection(type) {
  return {
    id: `s_${Math.random().toString(36).slice(2, 9)}`,
    type,
    visible: true,
    data: JSON.parse(JSON.stringify(EMPTY_DATA[type] || {})),
  };
}

export default function LandingEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState({ name: "BulkInvoice", nav_cta_text: "Sign In", nav_cta_href: "/login" });
  const [sections, setSections] = useState([]);
  const [adderType, setAdderType] = useState("features");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/landing");
      setBrand(data.brand || {});
      setSections(Array.isArray(data.sections) ? data.sections : []);
    } catch {
      toast.error("Failed to load landing config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/landing", { brand, sections });
      toast.success("Landing page saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (idx, patch) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const updateData = (idx, key, value) => {
    setSections((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, data: { ...(s.data || {}), [key]: value } } : s)),
    );
  };
  const updateFeature = (sidx, fidx, key, value) => {
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== sidx) return s;
        const feats = Array.isArray(s.data?.features) ? [...s.data.features] : [];
        feats[fidx] = { ...(feats[fidx] || {}), [key]: value };
        return { ...s, data: { ...s.data, features: feats } };
      }),
    );
  };
  const addFeature = (sidx) => {
    setSections((prev) =>
      prev.map((s, i) =>
        i !== sidx
          ? s
          : {
              ...s,
              data: {
                ...s.data,
                features: [...(s.data?.features || []), { title: "", body: "" }],
              },
            },
      ),
    );
  };
  const removeFeature = (sidx, fidx) => {
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== sidx) return s;
        const feats = (s.data?.features || []).filter((_, idx) => idx !== fidx);
        return { ...s, data: { ...s.data, features: feats } };
      }),
    );
  };

  const move = (idx, delta) => {
    setSections((prev) => {
      const n = [...prev];
      const j = idx + delta;
      if (j < 0 || j >= n.length) return prev;
      [n[idx], n[j]] = [n[j], n[idx]];
      return n;
    });
  };
  const remove = (idx) => {
    if (!window.confirm("Remove this section?")) return;
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };
  const add = () => {
    setSections((prev) => [...prev, newSection(adderType)]);
  };

  if (loading) {
    return (
      <div className="p-10 text-sm text-zinc-400 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading landing config…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action bar */}
      <Card className="p-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 text-sm text-zinc-600">
          <Pencil size={14} />
          <span>Edit everything shown on your public landing page at <code className="text-xs">/</code>.</span>
          <Button asChild variant="ghost" size="sm" className="h-7 ml-1 text-xs">
            <a href="/" target="_blank" rel="noreferrer">
              <ExternalLink size={12} className="mr-1" /> Open landing page
            </a>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="landing-reload" className="h-8">
            <RotateCcw size={13} className="mr-1" /> Reload
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="h-8 bg-blue-600 hover:bg-blue-700"
            data-testid="landing-save"
          >
            {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <Save size={13} className="mr-1" />}
            Save changes
          </Button>
        </div>
      </Card>

      {/* Brand */}
      <Card className="p-5">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Brand & nav</div>
        <div className="grid md:grid-cols-3 gap-4">
          <Labeled label="Brand name">
            <Input
              value={brand.name || ""}
              onChange={(e) => setBrand((b) => ({ ...b, name: e.target.value }))}
              data-testid="landing-brand-name"
            />
          </Labeled>
          <Labeled label="Top-right button text">
            <Input
              value={brand.nav_cta_text || ""}
              onChange={(e) => setBrand((b) => ({ ...b, nav_cta_text: e.target.value }))}
            />
          </Labeled>
          <Labeled label="Top-right button link">
            <Input
              value={brand.nav_cta_href || ""}
              onChange={(e) => setBrand((b) => ({ ...b, nav_cta_href: e.target.value }))}
              placeholder="/login"
            />
          </Labeled>
        </div>
      </Card>

      {/* Sections */}
      {sections.map((s, idx) => (
        <Card key={s.id || idx} className="p-5" data-testid={`landing-section-editor-${idx}`}>
          <div className="flex flex-wrap items-center gap-2 pb-4 border-b border-zinc-100 mb-4">
            <Badge variant="outline" className="text-xs">
              #{idx + 1}
            </Badge>
            <div className="font-semibold text-sm capitalize">{s.type}</div>
            <Badge
              variant="outline"
              className={`text-[10px] ${s.visible === false ? "bg-zinc-100 text-zinc-500" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
            >
              {s.visible === false ? "Hidden" : "Visible"}
            </Badge>
            <div className="ml-auto flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => updateSection(idx, { visible: s.visible === false })}
                title={s.visible === false ? "Show on landing" : "Hide from landing"}
                data-testid={`landing-toggle-visible-${idx}`}
              >
                {s.visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                title="Move up"
              >
                <ArrowUp size={14} />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => move(idx, 1)}
                disabled={idx === sections.length - 1}
                title="Move down"
              >
                <ArrowDown size={14} />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-zinc-400 hover:text-red-600"
                onClick={() => remove(idx)}
                title="Delete section"
                data-testid={`landing-remove-${idx}`}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>

          {/* Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            {(FIELD_SPECS[s.type] || []).map((f) => {
              if (f.type === "features") {
                const list = Array.isArray(s.data?.features) ? s.data.features : [];
                return (
                  <div key={f.k} className="md:col-span-2 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-zinc-600">{f.label}</div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => addFeature(idx)}
                        data-testid={`landing-feature-add-${idx}`}
                      >
                        <Plus size={12} className="mr-1" /> Add point
                      </Button>
                    </div>
                    {list.map((ft, fi) => (
                      <div
                        key={fi}
                        className="rounded-md border border-zinc-200 p-3 bg-zinc-50/50 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] text-zinc-500">Point {fi + 1}</div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-zinc-400 hover:text-red-600"
                            onClick={() => removeFeature(idx, fi)}
                          >
                            <Trash2 size={13} />
                          </Button>
                        </div>
                        <Input
                          placeholder="Title"
                          value={ft.title || ""}
                          onChange={(e) => updateFeature(idx, fi, "title", e.target.value)}
                          className="h-9 text-sm"
                        />
                        <Textarea
                          placeholder="Short description"
                          value={ft.body || ""}
                          onChange={(e) => updateFeature(idx, fi, "body", e.target.value)}
                          className="min-h-[60px] text-sm"
                        />
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <Labeled key={f.k} label={f.label} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  {f.type === "textarea" ? (
                    <Textarea
                      value={s.data?.[f.k] ?? ""}
                      onChange={(e) => updateData(idx, f.k, e.target.value)}
                      placeholder={f.placeholder}
                      className="min-h-[70px] text-sm"
                    />
                  ) : (
                    <Input
                      value={s.data?.[f.k] ?? ""}
                      onChange={(e) => updateData(idx, f.k, e.target.value)}
                      placeholder={f.placeholder}
                      className="h-9 text-sm"
                    />
                  )}
                </Labeled>
              );
            })}
          </div>
        </Card>
      ))}

      {/* Add section */}
      <Card className="p-5 border-dashed">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Add a new section</div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={adderType}
            onChange={(e) => setAdderType(e.target.value)}
            className="h-9 px-3 rounded-md border border-zinc-300 text-sm bg-white"
            data-testid="landing-add-type"
          >
            {SECTION_TYPES.map((t) => (
              <option key={t.type} value={t.type}>
                {t.label}
              </option>
            ))}
          </select>
          <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" onClick={add} data-testid="landing-add-section">
            <Plus size={14} className="mr-1" /> Add section
          </Button>
        </div>
      </Card>

      {/* Floating save (sticky) */}
      <div className="flex justify-end sticky bottom-6 z-10">
        <Button
          size="lg"
          onClick={save}
          disabled={saving}
          className="h-11 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20"
          data-testid="landing-save-sticky"
        >
          {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}

function Labeled({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
